import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useRollsQuery } from '../hooks/useRolls';
import { RollPanel } from '../components/RollPanel';
import { RollCard, type RollCardScan, type RollCardStatus } from '../components/RollCard';
import { DownloadTester } from '../components/DownloadTester';
import { sortRollsByCreatedAt } from '../utils/rollUtils';
import { usePageTitle } from '../hooks/usePageTitle';
import { useConfirm } from '../hooks/useConfirm';
import { useWindowFileDropGuard } from '../hooks/useWindowFileDropGuard';
import type { Roll } from '../types/roll';
import type { RollApiError } from '../hooks/useRolls';
import styles from './AdminUploadPage.module.css';

interface Scan extends RollCardScan {
  scan_id: string;
  thumbnail_ready: boolean;
  /** Present only on customer-created edit scans. */
  source_scan_id?: string;
}

interface OrderDetail {
  order_id: string;
  shopify_order_number: string;
  customer_id: string;
  customer_name?: string | null;
  scan_ready: boolean;
}

interface OrderDetailResponse {
  order: OrderDetail;
  scans: Scan[];
}

const UNASSIGNED_ROLL_ID = '__unassigned__';

export function AdminUploadPage() {
  usePageTitle('Admin — Upload');
  // A drop that misses a roll card would otherwise navigate away to the raw
  // TIFF, discarding every queued file and in-flight upload on the page.
  useWindowFileDropGuard();
  const { orderId } = useParams<{ orderId: string }>();
  const { handleUnauthorized } = useAdminAuth();
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();

  const [markingReady, setMarkingReady] = useState(false);
  const [markedReady, setMarkedReady] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rollStatuses, setRollStatuses] = useState<Record<string, RollCardStatus>>({});
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Poll order detail while any confirmed roll is still generating thumbnails.
  // A ref lets refetchInterval read the latest derived value without recreating
  // the query each render.
  const shouldPollRef = useRef(false);

  // Fetch rolls for this order
  const rollsQuery = useRollsQuery(orderId);
  const rolls = rollsQuery.data ? sortRollsByCreatedAt(rollsQuery.data) : [];

  const rollsError: { type: string; message: string } | null = rollsQuery.error
    ? {
        type: (rollsQuery.error as RollApiError).type ?? 'network',
        message: (rollsQuery.error as RollApiError).message ?? 'Could not load rolls',
      }
    : null;

  const { data, isLoading, error } = useQuery<OrderDetailResponse>({
    queryKey: ['admin', 'order', orderId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/admin/orders/${orderId}`, { credentials: 'include' });
      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        throw new Error('Unauthorized');
      }
      if (!res.ok) throw new Error('Failed to fetch order');
      return res.json();
    },
    refetchInterval: () => (shouldPollRef.current ? 5000 : false),
  });

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const handleRollStatusChange = useCallback((rollId: string, status: RollCardStatus) => {
    setRollStatuses((prev) => {
      const existing = prev[rollId];
      if (
        existing &&
        existing.uploading === status.uploading &&
        existing.pending === status.pending &&
        existing.failed === status.failed
      ) {
        return prev;
      }
      return { ...prev, [rollId]: status };
    });
  }, []);

  const handleUploaded = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'order', orderId] });
  }, [queryClient, orderId]);

  const handleConfirm = async () => {
    if (!orderId) return;
    setActionError(null);
    try {
      const res = await fetch(`/api/v1/admin/orders/${orderId}/scans/confirm`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }
      if (!res.ok) {
        setActionError('Failed to confirm uploads');
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'rolls', orderId] });
    } catch {
      setActionError('Failed to confirm uploads');
    }
  };

  const handleMarkReady = async () => {
    if (!orderId) return;
    setMarkingReady(true);
    setActionError(null);
    try {
      const res = await fetch(`/api/v1/admin/orders/${orderId}/ready`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }
      if (!res.ok) {
        setActionError('Failed to mark order as ready');
        return;
      }
      setMarkedReady(true);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'order', orderId] });
    } catch {
      setActionError('Failed to mark order as ready');
    } finally {
      setMarkingReady(false);
    }
  };

  const handleResetOrder = async () => {
    if (!orderId) return;
    const confirmedReset = await confirm({
      title: 'Reset order',
      message:
        'Reset this order?\n\nThis permanently deletes all uploaded scans, rolls, and generated ' +
        'images for the order and returns it to an awaiting-upload state. This cannot be undone.',
      confirmLabel: 'Reset',
      destructive: true,
    });
    if (!confirmedReset) return;

    setResetting(true);
    setActionError(null);
    try {
      const res = await fetch(`/api/v1/admin/orders/${orderId}/reset`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }
      if (!res.ok) {
        setActionError('Failed to reset order');
        return;
      }
      setMarkedReady(false);
      setRollStatuses({});
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'rolls', orderId] });
    } catch {
      setActionError('Failed to reset order');
    } finally {
      setResetting(false);
    }
  };

  if (isLoading) {
    return (
      <main className={styles.page}>
        <p>Loading order...</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className={styles.page}>
        <p className={styles.errorText}>Error loading order.</p>
        <Link to="/admin/orders" className={styles.backLink}>← Back to orders</Link>
      </main>
    );
  }

  const orderReady = data?.order.scan_ready ?? false;
  const isReadOnly = orderReady || markedReady;

  // Customer-created edits are managed from the customer gallery; exclude the
  // "My edits" roll and its scans from the upload/finalise workflow entirely
  // (they're visible via "Preview as customer"). Without this, an in-flight
  // edit render would block "mark ready" and its roll would offer upload UI.
  const labRolls = rolls.filter((r) => r.roll_type !== 'edits');
  // `source_scan_id` marks a derived edit copy. The roll-id prefix is a second
  // guard: the admin API omitted the field until recently, which silently made
  // this filter a no-op and dumped edits into "Unassigned" as if a lab upload
  // had lost its roll. The synthetic customer-edits roll is `edits-{orderId}`
  // (loupe-core `edits_roll_id`).
  const scans = (data?.scans ?? []).filter(
    (s) => !s.source_scan_id && !(s.roll_id ?? '').startsWith('edits-'),
  );

  // Scans grouped by their roll_id for per-card display.
  const scansByRoll = new Map<string, Scan[]>();
  for (const scan of scans) {
    const key = scan.roll_id && labRolls.some((r) => r.roll_id === scan.roll_id)
      ? scan.roll_id
      : UNASSIGNED_ROLL_ID;
    const list = scansByRoll.get(key) ?? [];
    list.push(scan);
    scansByRoll.set(key, list);
  }
  const sortByUploaded = (a: Scan, b: Scan) =>
    (a.uploaded_at ? Date.parse(a.uploaded_at) : 0) - (b.uploaded_at ? Date.parse(b.uploaded_at) : 0);
  for (const list of scansByRoll.values()) list.sort(sortByUploaded);
  const unassignedScans = scansByRoll.get(UNASSIGNED_ROLL_ID) ?? [];

  const totalScans = scans.length;
  const allThumbnailsReady = totalScans > 0 && scans.every((s) => s.thumbnail_ready);
  const anyUploading = Object.values(rollStatuses).some((s) => s.uploading);
  const anyPending = Object.values(rollStatuses).some((s) => s.pending > 0);

  // Per-roll confirmation state (only rolls that actually have scans matter).
  const rollsWithScans = labRolls.filter((r) => (scansByRoll.get(r.roll_id)?.length ?? 0) > 0);
  const unconfirmedRollsWithScans = rollsWithScans.filter((r) => !r.confirmed);
  const confirmedRollCount = rollsWithScans.length - unconfirmedRollsWithScans.length;
  const allRollsConfirmed = unconfirmedRollsWithScans.length === 0;
  // A roll is "processing" once confirmed but its thumbnails aren't all ready.
  const anyProcessing = rollsWithScans.some(
    (r) => r.confirmed && !(scansByRoll.get(r.roll_id) ?? []).every((s) => s.thumbnail_ready),
  );

  const canConfirmAll = unconfirmedRollsWithScans.length > 0 && !anyUploading && !anyPending;
  const canMarkReady = totalScans > 0 && allRollsConfirmed && allThumbnailsReady && !markedReady;

  const markReadyBlockerHint = markedReady
    ? null
    : !allRollsConfirmed
      ? `${unconfirmedRollsWithScans.length} roll${unconfirmedRollsWithScans.length === 1 ? '' : 's'} still to confirm.`
      : !allThumbnailsReady
        ? 'Waiting for all thumbnails to finish generating.'
        : null;

  // Drive polling from render: keep refreshing while anything is processing.
  shouldPollRef.current = anyProcessing && !markedReady;

  return (
    <main className={styles.page}>
      <Link to="/admin/orders" className={styles.backLink}>← Back to orders</Link>
      <h1 className={styles.heading}>Upload Scans — Order {data?.order.shopify_order_number}</h1>
      <p className={styles.customerInfo}>Customer: {data?.order.customer_name || data?.order.customer_id}</p>
      <p>
        <Link
          to={`/admin/orders/${orderId}/preview`}
          target="_blank"
          rel="noopener"
          className={styles.previewLink}
        >
          Preview as customer ↗
        </Link>
      </p>

      {markedReady && (
        <p className={styles.successMessage}>Order marked as ready. Customer has been notified.</p>
      )}

      {orderReady && !markedReady && (
        <p className={styles.readyMessage}>This order is already marked as ready.</p>
      )}

      {/* Roll creation — available at any point during upload */}
      {!isReadOnly && (
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>Rolls</h2>
          <RollPanel orderId={orderId!} onRollCreated={() => { /* roll list refetches via query invalidation */ }} />
          {rollsError && (
            <p className={styles.errorText} role="alert">{rollsError.message}</p>
          )}
        </section>
      )}

      {/* Per-roll upload cards */}
      {labRolls.length === 0 && !rollsQuery.isLoading && !isReadOnly ? (
        <p className={styles.rollPrompt}>Create a roll above to start uploading scans.</p>
      ) : (
        labRolls.map((roll: Roll) => (
          <RollCard
            key={roll.roll_id}
            orderId={orderId!}
            roll={roll}
            scans={scansByRoll.get(roll.roll_id) ?? []}
            readOnly={isReadOnly}
            onStatusChange={handleRollStatusChange}
            onUploaded={handleUploaded}
          />
        ))
      )}

      {/* Legacy scans with no matching roll stay visible, read-only */}
      {unassignedScans.length > 0 && (
        <RollCard
          orderId={orderId!}
          roll={{ roll_id: UNASSIGNED_ROLL_ID, roll_name: 'Unassigned' }}
          scans={unassignedScans}
          readOnly
        />
      )}

      {/* Order-level footer: confirm remaining rolls → mark ready */}
      {!isReadOnly && totalScans > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>Finalise order</h2>
          <p className={styles.statusSummary}>
            {totalScans} scan{totalScans === 1 ? '' : 's'} across {rollsWithScans.length} roll
            {rollsWithScans.length === 1 ? '' : 's'} · {confirmedRollCount}/{rollsWithScans.length} confirmed
          </p>

          {anyProcessing && (
            <p className={styles.waitingText}>Generating thumbnails…</p>
          )}

          <div className={styles.actions}>
            {unconfirmedRollsWithScans.length > 0 && (
              <button onClick={handleConfirm} disabled={!canConfirmAll}>
                Confirm all remaining ({unconfirmedRollsWithScans.length})
              </button>
            )}
            <button onClick={handleMarkReady} disabled={!canMarkReady || markingReady} className={styles.markReadyBtn}>
              {markingReady ? 'Marking Ready...' : 'Mark Ready'}
            </button>
          </div>

          {unconfirmedRollsWithScans.length > 0 && anyPending && (
            <p className={styles.helperText}>Upload or clear pending files, then confirm each roll.</p>
          )}
          {markReadyBlockerHint && (
            <p className={styles.helperText}>{markReadyBlockerHint}</p>
          )}
          {actionError && (
            <p className={styles.errorText} role="alert">{actionError}</p>
          )}
        </section>
      )}

      {/* Admin-only: run the real download pipeline to test what the customer gets */}
      {totalScans > 0 && <DownloadTester orderId={orderId!} />}

      {/* Danger zone — reset the order back to an awaiting-upload state */}
      <section className={styles.dangerZone}>
        <h2 className={styles.sectionHeading}>Danger zone</h2>
        <p className={styles.helperText}>
          Reset this order to an awaiting-upload state. This permanently deletes all uploaded
          scans, rolls, and generated images for the order. This cannot be undone.
        </p>
        <button type="button" onClick={handleResetOrder} disabled={resetting} className={styles.resetBtn}>
          {resetting ? 'Resetting…' : 'Reset Order'}
        </button>
      </section>

      {confirmDialog}
    </main>
  );
}
